import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DkDialogService, DkSelect, DkTooltip, type DkSelectOption } from '@dkds/ng-components';
import { LucideAngularModule } from 'lucide-angular';
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  HUBSPOT_SETTINGS_DATA,
  HUBSPOT_SETTINGS_TITLE_ID,
  HubspotSettingsData,
  HubspotSettingsDialogComponent,
  HubspotSettingsResult,
} from '../../../features/dashboard/components/hubspot-settings-dialog/hubspot-settings-dialog.component';
import { GoogleChatSpaceDto } from '../../../features/dashboard/models/google-chat.dto';
import { HubspotConnectionDto, HubspotPortalLinkDto } from '../../../features/dashboard/models/hubspot.dto';
import { OrgMemberDetailDto } from '../../../features/dashboard/models/organization.dto';
import { ProjectMemberDetailDto } from '../../../features/dashboard/models/project.dto';
import {
  SlackChannelDto,
  SlackInstallationDto,
  SlackProjectChannelRequestDto,
} from '../../../features/dashboard/models/slack.dto';
import { TimeTrackingMode } from '../../../features/dashboard/models/time-entry.dto';
import { DashboardService } from '../../../features/dashboard/services/dashboard.service';
import { GoogleChatService } from '../../../features/dashboard/services/google-chat.service';
import { HubspotService } from '../../../features/dashboard/services/hubspot.service';
import { OrganizationDashboardService } from '../../../features/dashboard/services/organization-dashboard.service';
import { ProjectService as ProjectApiService } from '../../../features/dashboard/services/project.service';
import { RequestService } from '../../../features/dashboard/services/request.service';
import { SlackService } from '../../../features/dashboard/services/slack.service';
import { WebflowService } from '../../../features/dashboard/services/webflow.service';
import { OnboardingService } from '../../../features/onboarding/services/onboarding.service';
import { ProjectsDataDto } from '../../models/dashboard.dto';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { NotificationService } from '../../services/notification.service';
import { ProjectService } from '../../services/project.service';
import { SessionService } from '../../services/session.service';
import { STORAGE_KEYS } from '../../services/storage-keys';
import { StorageService } from '../../services/storage.service';
import { ToastService } from '../../toast/toast.service';
import { SingleDropdownOption } from '../inputs/declunk-single-dropdown/declunk-single-dropdown.component';

/** View model for one global notification preference row — mirrors NotificationsPanelComponent's NotifPrefVm. */
interface NotifPrefVm {
  type: string;
  label: string;
  description: string;
  enabled: boolean; // In-app
  emailEnabled: boolean; // Email
}

@Component({
  selector: 'app-settings-modal',
  imports: [FormsModule, DatePipe, LucideAngularModule, A11yModule, OverlayModule, DkTooltip, DkSelect, RouterModule],
  templateUrl: './settings-modal.component.html',
  styleUrl: './settings-modal.component.scss',
})
export class SettingsModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() projects: ProjectsDataDto[] = [];
  @Input() initialSection: 'projects-general' | 'workspace-general' | null = null;
  @Input() initialProjectId?: string;
  /**
   * Set when the modal is opening on the back half of an OAuth round trip, so the user lands on
   * the setup they left to authorize rather than on the row they already pressed Connect from.
   */
  @Input() initialIntegration: 'slack' | 'hubspot' | null = null;
  /** The Slack workspace link that round trip installed, preselected once its status comes back. */
  @Input() initialSlackWorkspaceLinkId?: string;
  @Output() closeModal = new EventEmitter<void>();
  /** Emitted with the project id after it's been deleted or left, so the parent can drop it and redirect. */
  @Output() projectRemoved = new EventEmitter<string>();

  @ViewChild('settingsContent') settingsContentRef?: ElementRef<HTMLElement>;

  private readonly dialogs = inject(DkDialogService);
  private readonly hubspotService = inject(HubspotService);
  private readonly storage = inject(StorageService);

  public activeSection: 'account' | 'notifications' | 'workspace-general' | 'projects-general' = 'account';

  public firstName = '';
  public lastName = '';

  public passwordFormOpen = false;
  public currentPassword = '';
  public newPassword = '';
  public confirmPassword = '';
  public passwordSubmitting = false;

  public avatarPreview: string | null = null;
  public isUploadingAvatar = false;

  // Notifications section — global notification preferences (In-app / Email per type).
  public settingsPrefs: NotifPrefVm[] = [];
  public notifPrefsLoading = false;
  public notifPrefsError = false;
  private notifPrefsLoaded = false;

  private static readonly INVOLVED_PREF_ORDER = [
    'task.assigned',
    'request.review_requested',
    'request.updated',
    'request.hold_released',
    'request.deleted',
    'comment.created',
  ];
  private static readonly GENERAL_PREF_ORDER = [
    'site.published',
    'comment.mention',
    'request.created',
    'project.watch_all',
    'webflow.sync_started',
    'webflow.sync_completed',
    'webflow.sync_failed',
  ];

  // Workspace → General section — icon/name, org members (client-side paginated), join-workspace toggle.
  public workspaceName = '';
  public workspaceIconPreview: string | null = null;
  public isUploadingIcon = false;

  public orgMembers: OrgMemberDetailDto[] = [];
  public membersLoading = false;
  public membersError = false;
  private workspaceGeneralLoaded = false;

  // Editable member-role dropdown (org owner/admin only) — role options (owner excluded),
  // per-row OrgMemberRole id resolution, and per-row saving state.
  public orgRoleOptions: SingleDropdownOption[] = [];
  public orgRolesLoading = false;
  private orgRolesLoaded = false;
  private orgMemberRoleRowIds = new Map<string, string>();
  public savingOrgMemberRoleIds = new Set<string>();

  public pageSize = 4;
  public currentPage = 1;
  public readonly pageSizeOptions = [4, 8, 12, 20];

  public allowDomainJoin = true;
  public domainJoinLoading = false;
  public isUpdatingDomainJoin = false;

  // Workspace time tracking mode — read from the organization payload, written via its own endpoint.
  public timeTracking: TimeTrackingMode = 'off';
  public isUpdatingTimeTracking = false;
  public readonly timeTrackingOptions: DkSelectOption[] = [
    { value: 'off', label: 'Off' },
    { value: 'optional', label: 'Optional' },
    { value: 'required', label: 'Required' },
  ];

  public linkJoinEnabled = false;
  public workspaceInvitationCode: string | null = null;
  public isUpdatingLinkJoin = false;

  // Projects → General section.
  public selectedProjectId: string | null = null;
  public projectName = '';
  public projectIconPreview: string | null = null;
  public isUploadingProjectIcon = false;
  public projectDetailLoading = false;
  public defaultAssigneeId: string | null = null;
  public isSavingDefaultAssignee = false;
  /**
   * Emits when the selected project changes or the modal closes, ending what was in flight for the
   * project being left: a late answer for it would otherwise land on the one now on screen.
   */
  private readonly projectSwitch$ = new Subject<void>();

  // Members table rows, which also feed the default-assignee dropdown.
  public projectMembersDetail: ProjectMemberDetailDto[] = [];
  public projectMembersLoading = false;
  public projectMembersError = false;
  public openMemberMenuId: string | null = null;
  /** Below the row's actions button, flipping above it when the table runs out of room. */
  public readonly memberMenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];

  // Editable member-role dropdown (project admins only) — role options, per-row ProjectMemberRole
  // id resolution, and per-row saving state.
  public projectRoleOptions: SingleDropdownOption[] = [];
  public projectRolesLoading = false;
  private projectRolesLoaded = false;
  private readonly roleLabels: Record<string, string> = {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    viewer: 'Viewer',
  };
  private memberRoleRowIds = new Map<string, string>();
  public savingMemberRoleIds = new Set<string>();

  public projectMemberPageSize = 4;
  public projectMemberCurrentPage = 1;

  // Projects → Danger zone: delete project (type-project-name-to-confirm).
  public deleteProjectConfirmOpen = false;
  public deleteProjectConfirmText = '';
  public isDeletingProject = false;
  public deleteProjectError: string | null = null;

  // Projects → Danger zone: leave project (self-service, any role).
  public isLeavingProject = false;

  // Project invite link — GET/PATCH /api/projects/{id}/link-settings/.
  public projectLinkJoinEnabled = false;
  public projectInvitationCode: string | null = null;
  public isUpdatingProjectLinkJoin = false;
  public isRegeneratingProjectLink = false;

  public readonly projectDefaultRoleOptions: SingleDropdownOption[] = [
    { id: 'member', label: 'Member' },
    { id: 'admin', label: 'Admin' },
    { id: 'viewer', label: 'Viewer' },
  ];
  public projectDefaultRole: 'admin' | 'member' | 'viewer' | null = 'member';
  public isSavingProjectDefaultRole = false;

  // Team & Roles — no backend for these yet, visual-only toggles.
  public autoExpireInviteLinks = true;
  public allowMembersToInvite = true;

  // Integrations.
  public slackConnected = false;
  public slackLoading = false;
  /** The dialog where the whole of a project's Slack setup is edited — workspace, channel, rules. */
  public slackSettingsOpen = false;
  /** Every Slack workspace this project's organization is linked to. */
  public slackWorkspaceLinks: SlackInstallationDto[] = [];
  /**
   * The link this project is pointed at, carried on the channel list and the save. Only ever set
   * when the organization has several links: with one there is nothing to choose, and leaving it
   * empty keeps those requests what a row that knew nothing about links would send.
   */
  public slackSelectedWorkspaceLinkId = '';
  public slackChannels: SlackChannelDto[] = [];
  public slackChannelsLoading = false;
  public slackSelectedChannelId = '';
  public slackChannelName = '';
  public slackNotifyOnCreation = true;
  public slackNotifyOnCompletion = true;
  /**
   * Checked, the project keeps whatever workspace it is on but loses its channel, so nothing is
   * posted for it. That is a different thing from Disconnect, which drops the workspace too.
   */
  public slackNotificationsDisabled = false;
  public slackConnecting = false;
  /**
   * What the backend currently holds, so the dialog opens on the saved setup and Cancel leaves
   * nothing behind.
   */
  private slackSavedWorkspaceLinkId = '';
  private slackSavedChannelId = '';
  private slackSavedNotifyOnCreation = true;
  private slackSavedNotifyOnCompletion = true;
  /** An OAuth return waiting on the status call, which is what says the new workspace exists. */
  private pendingSlackSettings = false;
  public hubspotLoading = false;
  /** Set from Connect until the browser leaves for HubSpot's install screen. */
  public hubspotConnecting = false;
  /** The organization's live HubSpot accounts. None means Connect has to install one first. */
  public hubspotPortalLinks: HubspotPortalLinkDto[] = [];
  public hubspotConnection: HubspotConnectionDto | null = null;
  /** An OAuth return waiting on the statuses, which say whether there is a project left to attach. */
  private pendingHubspotSettings = false;
  public webflowConnected = false;
  public webflowLoading = false;
  public webflowConnectedSince = '';
  /** Whether the organization has Google Chat at all; the row is hidden when it doesn't. */
  public gchatEnabled = true;
  public gchatConnected = false;
  public gchatLoading = false;
  public gchatPickerOpen = false;
  public gchatSpaces: GoogleChatSpaceDto[] = [];
  public gchatSpacesLoading = false;
  public gchatSelectedSpaceName = '';
  public gchatConnecting = false;

  public isProd = environment.production;

  constructor(
    private readonly session: SessionService,
    private readonly onboardingService: OnboardingService,
    private readonly dashboardService: DashboardService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly notificationService: NotificationService,
    private readonly projectService: ProjectService,
    private readonly organizationDashboardService: OrganizationDashboardService,
    private readonly projectApiService: ProjectApiService,
    private readonly requestService: RequestService,
    private readonly slackService: SlackService,
    private readonly googleChatService: GoogleChatService,
    private readonly webflowService: WebflowService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && !this.isOpen) {
      // The next opening reloads the project it lands on rather than showing this one's leftovers.
      this.projectSwitch$.next();
      this.selectedProjectId = null;
    }
    if (!this.isOpen) return;

    // Armed on the input changing rather than on every pass: the projects arrive after the
    // request that opened the modal, and re-arming there would reopen a dialog already dismissed.
    if (changes['initialIntegration'] && this.initialIntegration === 'slack') {
      this.pendingSlackSettings = true;
    }
    if (changes['initialIntegration'] && this.initialIntegration === 'hubspot') {
      this.pendingHubspotSettings = true;
    }

    // Only an opening sets the modal up, or the projects arriving for one that is waiting on them.
    // The dashboard replaces `projects` on every refresh, and treating that pass as an opening threw
    // the user back to Account, or onto the first project, and reloaded everything.
    const opening = !!(changes['isOpen'] || changes['initialSection'] || changes['initialProjectId']);
    const projectsArrived =
      !!changes['projects'] && this.initialSection === 'projects-general' && !this.selectedProjectId;
    if (!opening && !projectsArrived) return;

    if (this.initialSection === 'projects-general') {
      this.activeSection = 'projects-general';
      const project = this.projects.find((p) => p.id === this.initialProjectId) ?? this.projects[0];
      if (project) {
        this.selectProject(project);
      }
      if (this.settingsContentRef) {
        this.settingsContentRef.nativeElement.scrollTop = 0;
      }
      setTimeout(() => {
        this.settingsContentRef?.nativeElement
          .querySelector('#integrationsSection')
          ?.scrollIntoView({ block: 'start' });
      });
      return;
    }

    this.activeSection = 'account';
    this.firstName = this.session.profile()?.first_name ?? '';
    this.lastName = this.session.profile()?.last_name ?? '';
    this.avatarPreview = null;
    this.workspaceName = this.session.organizationName();
    this.workspaceIconPreview = null;
    if (this.initialSection === 'workspace-general') {
      this.selectSection('workspace-general');
    }
  }

  selectSection(section: 'account' | 'notifications' | 'workspace-general' | 'projects-general'): void {
    if (section === 'projects-general' && this.projects.length === 0) return;
    this.activeSection = section;
    if (this.settingsContentRef) {
      this.settingsContentRef.nativeElement.scrollTop = 0;
    }
    if (section === 'notifications' && !this.notifPrefsLoaded) {
      this.loadNotifPrefs();
    }
    if (section === 'workspace-general' && !this.workspaceGeneralLoaded) {
      this.workspaceGeneralLoaded = true;
      this.loadOrgMembers();
      this.loadDomainJoinSetting();
      this.loadOrgRoles();
    }
    if (section === 'projects-general' && !this.selectedProjectId && this.projects.length > 0) {
      this.selectProject(this.projects[0]);
    }
  }

  get groupedSettingsPrefs(): { label: string; prefs: NotifPrefVm[] }[] {
    const orderOf = (order: string[], type: string) => {
      const i = order.indexOf(type);
      return i === -1 ? order.length : i;
    };
    const involvedOrder = SettingsModalComponent.INVOLVED_PREF_ORDER;
    const involved = this.settingsPrefs
      .filter((p) => involvedOrder.includes(p.type))
      .sort((a, b) => orderOf(involvedOrder, a.type) - orderOf(involvedOrder, b.type));
    const generalOrder = SettingsModalComponent.GENERAL_PREF_ORDER;
    const general = this.settingsPrefs
      .filter((p) => !involvedOrder.includes(p.type))
      .sort((a, b) => orderOf(generalOrder, a.type) - orderOf(generalOrder, b.type));
    return [
      { label: "Requests you're a part of", prefs: involved },
      { label: 'General Project Activity', prefs: general },
    ].filter((g) => g.prefs.length > 0);
  }

  private loadNotifPrefs(): void {
    this.notifPrefsLoading = true;
    this.notifPrefsError = false;
    this.notificationService.getPreferences(this.projectService.projectId || undefined).subscribe({
      next: (prefs) => {
        this.settingsPrefs = prefs.map((p) => ({
          type: p.type,
          label: p.label,
          description: p.description,
          enabled: p.enabled,
          emailEnabled: p.send_emails,
        }));
        this.notifPrefsLoading = false;
        this.notifPrefsLoaded = true;
      },
      error: () => {
        this.notifPrefsError = true;
        this.notifPrefsLoading = false;
      },
    });
  }

  togglePref(pref: NotifPrefVm): void {
    const previous = pref.enabled;
    pref.enabled = !pref.enabled;
    this.savePref(pref, () => {
      pref.enabled = previous;
    });
  }

  toggleEmail(pref: NotifPrefVm): void {
    const previous = pref.emailEnabled;
    pref.emailEnabled = !pref.emailEnabled;
    this.savePref(pref, () => {
      pref.emailEnabled = previous;
    });
  }

  private savePref(pref: NotifPrefVm, revert: () => void): void {
    // Preferences are GLOBAL (apply to every project). Only the watch-all opt-in is
    // legitimately project-scoped. `|| undefined` (not ??): projectId is '' when unset,
    // and the backend treats an omitted `project` as the global row.
    const project = pref.type === 'project.watch_all' ? this.projectService.projectId || undefined : undefined;
    this.notificationService
      .updatePreferences([{ type: pref.type, enabled: pref.enabled, send_emails: pref.emailEnabled, project }])
      .subscribe({
        next: (saved) => {
          const persisted = saved?.find((s) => s.type === pref.type);
          if (persisted) {
            pref.enabled = persisted.enabled;
            if (persisted.send_emails !== undefined) {
              pref.emailEnabled = persisted.send_emails;
            }
          }
        },
        error: () => revert(),
      });
  }

  // ── Workspace → General ──────────────────────────────────────
  get workspaceIcon(): string | null {
    return this.workspaceIconPreview ?? this.session.organizationLogo();
  }

  get workspaceInitials(): string {
    return this.session.orgInitials();
  }

  get totalMemberPages(): number {
    return Math.max(1, Math.ceil(this.orgMembers.length / this.pageSize));
  }

  get pagedMembers(): OrgMemberDetailDto[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.orgMembers.slice(start, start + this.pageSize);
  }

  get memberPageRangeLabel(): string {
    if (this.orgMembers.length === 0) return 'No members';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.orgMembers.length);
    return `Showing ${start}–${end} of ${this.orgMembers.length} members`;
  }

  get memberPageNumbers(): number[] {
    return Array.from({ length: this.totalMemberPages }, (_, i) => i + 1);
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalMemberPages) return;
    this.currentPage = page;
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
  }

  private loadOrgMembers(): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.membersLoading = true;
    this.membersError = false;
    this.organizationDashboardService.getOrgMembersDetail(orgId).subscribe({
      next: (res) => {
        this.orgMembers = res.data ?? [];
        this.membersLoading = false;
      },
      error: () => {
        this.membersError = true;
        this.membersLoading = false;
      },
    });
  }

  // ── Workspace → General: editable member role dropdown (owner/admin only) ──
  get canEditOrgMemberRoles(): boolean {
    return this.session.isOrganizationAdmin();
  }

  private loadOrgRoles(): void {
    if (this.orgRolesLoaded) return;
    this.orgRolesLoading = true;
    this.projectApiService.getRoles().subscribe({
      next: (res) => {
        this.orgRoleOptions = (res.data ?? [])
          .filter((r) => r.name !== 'owner')
          .map((r) => ({ id: r.id, label: this.roleLabels[r.name] ?? r.name }));
        this.orgRolesLoaded = true;
        this.orgRolesLoading = false;
      },
      error: () => {
        this.orgRolesLoading = false;
      },
    });
  }

  /** Rows show as soon as both the members and the role options are in, so the selects never pop in afterwards. */
  get membersTableLoading(): boolean {
    return this.membersLoading || (this.canEditOrgMemberRoles && this.orgRolesLoading);
  }

  /**
   * The OrgMemberRole id a role PATCH targets. The members list doesn't carry it, so it's looked up
   * on the first change for a member and cached — never per page, which made the selects lag behind
   * the rows. Errors (404 when unassigned) reach the caller's error handler.
   */
  private orgMemberRoleRowId$(memberId: string): Observable<string> {
    const cached = this.orgMemberRoleRowIds.get(memberId);
    if (cached) return of(cached);
    return this.organizationDashboardService.getOrgMemberRole(memberId).pipe(
      map((res) => {
        if (!res?.data) throw new Error('This member has no workspace role to change.');
        this.orgMemberRoleRowIds.set(memberId, res.data.id);
        return res.data.id;
      }),
    );
  }

  isSavingOrgMemberRole(memberId: string): boolean {
    return this.savingOrgMemberRoleIds.has(memberId);
  }

  orgMemberRoleOptionId(member: OrgMemberDetailDto): string | null {
    return this.orgRoleOptions.find((o) => o.label.toLowerCase() === member.role)?.id ?? null;
  }

  onOrgMemberRoleChange(member: OrgMemberDetailDto, roleId: string | null): void {
    if (!roleId) return;

    const previousRole = member.role;
    const optimisticLabel = this.orgRoleOptions.find((o) => o.id === roleId)?.label.toLowerCase();
    member.role = (optimisticLabel ?? previousRole) as OrgMemberDetailDto['role'];
    this.savingOrgMemberRoleIds.add(member.id);

    this.orgMemberRoleRowId$(member.id)
      .pipe(switchMap((rowId) => this.organizationDashboardService.updateOrgMemberRole(rowId, roleId)))
      .subscribe({
        next: (res) => {
          this.savingOrgMemberRoleIds.delete(member.id);
          const confirmedLabel = this.orgRoleOptions.find((o) => o.id === res?.data?.role)?.label.toLowerCase();
          if (confirmedLabel) member.role = confirmedLabel as OrgMemberDetailDto['role'];
          this.toastService.success({ message: 'Member role updated.' });
        },
        error: (err) => {
          this.savingOrgMemberRoleIds.delete(member.id);
          member.role = previousRole;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  private loadDomainJoinSetting(): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.domainJoinLoading = true;
    this.organizationDashboardService.getOrganization(orgId).subscribe({
      next: (res) => {
        this.domainJoinLoading = false;
        if (res?.status === 'success' && res.data) {
          this.allowDomainJoin = res.data.allow_domain_join !== false;
          this.linkJoinEnabled = !!res.data.link_join_enabled;
          this.workspaceInvitationCode = res.data.invitation_code ?? null;
          this.timeTracking = res.data.time_tracking ?? 'off';
        }
      },
      error: () => {
        this.domainJoinLoading = false;
      },
    });
  }

  toggleLinkJoin(): void {
    const orgId = this.session.organizationId();
    if (!orgId || this.isUpdatingLinkJoin) return;

    const newValue = !this.linkJoinEnabled;
    this.linkJoinEnabled = newValue;
    this.isUpdatingLinkJoin = true;

    this.organizationDashboardService.updateOrganization(orgId, { link_join_enabled: newValue }).subscribe({
      next: (res) => {
        this.isUpdatingLinkJoin = false;
        if (res?.status === 'success' && res.data) {
          this.linkJoinEnabled = !!res.data.link_join_enabled;
          this.workspaceInvitationCode = res.data.invitation_code ?? null;
        }
      },
      error: (err) => {
        this.isUpdatingLinkJoin = false;
        this.linkJoinEnabled = !newValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  copyWorkspaceInviteLink(): void {
    if (!this.workspaceInvitationCode) return;
    const url = `${window.location.origin}/join?code=${this.workspaceInvitationCode}`;
    navigator.clipboard.writeText(url).then(
      () => this.toastService.success({ message: 'Invite link copied.' }),
      () => this.toastService.error({ message: 'Could not copy the link.' }),
    );
  }

  toggleDomainJoin(): void {
    const orgId = this.session.organizationId();
    if (!orgId || this.isUpdatingDomainJoin) return;

    const newValue = !this.allowDomainJoin;
    this.allowDomainJoin = newValue;
    this.isUpdatingDomainJoin = true;

    this.organizationDashboardService.updateOrganization(orgId, { allow_domain_join: newValue }).subscribe({
      next: (res) => {
        this.isUpdatingDomainJoin = false;
        if (res?.status === 'success' && res.data) {
          this.allowDomainJoin = res.data.allow_domain_join !== false;
        }
      },
      error: (err) => {
        this.isUpdatingDomainJoin = false;
        this.allowDomainJoin = !newValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  /** Workspace owner/admin, or the admin of any project in the workspace — everyone else gets a 403. */
  get canEditTimeTracking(): boolean {
    return this.session.isOrganizationAdmin() || this.projects.some((p) => p.user_role === 'admin');
  }

  onTimeTrackingChange(mode: TimeTrackingMode): void {
    const orgId = this.session.organizationId();
    const previousMode = this.timeTracking;
    if (!orgId || this.isUpdatingTimeTracking || mode === previousMode) return;

    this.timeTracking = mode;
    this.isUpdatingTimeTracking = true;

    this.organizationDashboardService.updateTimeTracking(orgId, mode).subscribe({
      next: (res) => {
        this.isUpdatingTimeTracking = false;
        if (res?.status === 'success' && res.data) {
          this.timeTracking = res.data.time_tracking;
        }
        this.toastService.success({ message: 'Time tracking updated.' });
      },
      error: (err) => {
        this.isUpdatingTimeTracking = false;
        this.timeTracking = previousMode;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  saveWorkspaceName(): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    const value = this.workspaceName.trim();
    const previousValue = this.session.organizationName();
    if (value === previousValue || !value) {
      this.workspaceName = previousValue;
      return;
    }

    this.organizationDashboardService.updateOrganization(orgId, { name: value }).subscribe({
      next: () => {
        this.session.patchOrganization({ name: value });
        this.toastService.success({ message: 'Workspace updated.' });
      },
      error: (err) => {
        this.workspaceName = previousValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  onWorkspaceIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadWorkspaceIcon(file);
    }
  }

  private uploadWorkspaceIcon(file: File): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error({ message: 'Please choose an image file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => (this.workspaceIconPreview = e.target?.result as string);
    reader.readAsDataURL(file);

    this.isUploadingIcon = true;
    this.organizationDashboardService
      .getPresignedUploadUrl({
        filename: file.name,
        content_type: file.type,
        prefix: 'workspaces',
        organization_id: orgId,
      })
      .subscribe({
        next: (resp) => {
          if (resp.status === 'success' && resp.data) {
            const key = resp.data.key;
            this.organizationDashboardService.uploadFileToPresignedUrl(resp.data.upload_url, file).subscribe({
              next: () => {
                this.organizationDashboardService.updateOrganization(orgId, { logo_key: key }).subscribe({
                  next: (updateResp) => {
                    const logo = updateResp.data?.logo ?? null;
                    this.session.patchOrganization({ logo });
                    this.workspaceIconPreview = logo;
                    this.isUploadingIcon = false;
                    this.toastService.success({ message: 'Workspace icon updated.' });
                  },
                  error: (err) => {
                    this.isUploadingIcon = false;
                    this.workspaceIconPreview = null;
                    this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
                  },
                });
              },
              error: (err) => {
                this.isUploadingIcon = false;
                this.workspaceIconPreview = null;
                this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
              },
            });
          } else {
            this.isUploadingIcon = false;
            this.workspaceIconPreview = null;
            this.toastService.error({ message: 'Could not start the upload. Please try again.' });
          }
        },
        error: (err) => {
          this.isUploadingIcon = false;
          this.workspaceIconPreview = null;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  // ── Projects → General ────────────────────────────────────────
  private lastSavedProjectName = '';

  get selectedProject(): ProjectsDataDto | undefined {
    return this.projects.find((p) => p.id === this.selectedProjectId);
  }

  get selectedProjectName(): string {
    return this.selectedProject?.name ?? '';
  }

  get projectIcon(): string | null {
    return this.projectIconPreview ?? this.selectedProject?.logo ?? null;
  }

  get projectInitials(): string {
    const name = this.projectName || this.selectedProjectName;
    if (!name) return '';
    const words = name.trim().split(' ');
    if (words.length > 1) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  /**
   * Whether the user is an admin of the selected project, the only role the backend lets change
   * its settings. Read from the projects list, which carries each project's role, rather than from
   * ProjectMembershipService: that holds the role in the project the app is on, and loading the
   * one picked here into it changed the role every page behind the modal was using.
   */
  get isProjectAdmin(): boolean {
    return this.selectedProject?.user_role === 'admin';
  }

  get activeProjectMembers(): ProjectMemberDetailDto[] {
    return this.projectMembersDetail.filter((m) => m.status === 'active');
  }

  get defaultAssigneeMember(): ProjectMemberDetailDto | undefined {
    return this.projectMembersDetail.find((m) => m.user_id === this.defaultAssigneeId);
  }

  get projectMemberTotalPages(): number {
    return Math.max(1, Math.ceil(this.projectMembersDetail.length / this.projectMemberPageSize));
  }

  get pagedProjectMembers(): ProjectMemberDetailDto[] {
    const start = (this.projectMemberCurrentPage - 1) * this.projectMemberPageSize;
    return this.projectMembersDetail.slice(start, start + this.projectMemberPageSize);
  }

  get projectMemberPageRangeLabel(): string {
    if (this.projectMembersDetail.length === 0) return 'No members';
    const start = (this.projectMemberCurrentPage - 1) * this.projectMemberPageSize + 1;
    const end = Math.min(this.projectMemberCurrentPage * this.projectMemberPageSize, this.projectMembersDetail.length);
    return `Showing ${start}–${end} of ${this.projectMembersDetail.length} members`;
  }

  get projectMemberPageNumbers(): number[] {
    return Array.from({ length: this.projectMemberTotalPages }, (_, i) => i + 1);
  }

  setProjectMemberPage(page: number): void {
    if (page < 1 || page > this.projectMemberTotalPages) return;
    this.projectMemberCurrentPage = page;
  }

  setProjectMemberPageSize(size: number): void {
    this.projectMemberPageSize = size;
    this.projectMemberCurrentPage = 1;
  }

  selectProject(project: ProjectsDataDto): void {
    if (project.id === this.selectedProjectId) return;
    this.projectSwitch$.next();

    this.selectedProjectId = project.id;
    this.projectName = project.name;
    this.lastSavedProjectName = project.name;
    this.projectIconPreview = null;
    this.defaultAssigneeId = null;
    this.projectMembersDetail = [];
    this.projectMemberCurrentPage = 1;
    this.gchatPickerOpen = false;
    this.slackSettingsOpen = false;
    this.slackChannels = [];
    this.slackSelectedChannelId = '';
    this.slackSelectedWorkspaceLinkId = '';
    this.hubspotPortalLinks = [];
    this.hubspotConnection = null;
    this.hubspotConnecting = false;
    this.projectLinkJoinEnabled = false;
    this.projectInvitationCode = null;
    this.projectDefaultRole = null;
    this.memberRoleRowIds.clear();
    this.savingMemberRoleIds.clear();

    this.loadProjectDetail(project.id);
    this.loadProjectMembersDetail(project.id);
    this.loadIntegrationStatuses(project.id);
    // Only an admin is offered the role selects the options fill.
    if (this.isProjectAdmin) {
      this.loadProjectRoles();
    }
  }

  private syncProjectListEntry(projectId: string, patch: Partial<ProjectsDataDto>): void {
    const entry = this.projects.find((p) => p.id === projectId);
    if (entry) {
      Object.assign(entry, patch);
    }
  }

  private loadProjectDetail(projectId: string): void {
    this.projectDetailLoading = true;
    this.projectApiService
      .getProject(projectId)
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe({
        next: (res) => {
          this.projectDetailLoading = false;
          if (res?.status === 'success' && res.data) {
            this.projectName = res.data.name;
            this.lastSavedProjectName = res.data.name;
            this.defaultAssigneeId = res.data.default_assignee?.user_id ?? null;
            // The project carries its invite-link settings: /link-settings/ answers with this same payload.
            this.projectLinkJoinEnabled = !!res.data.link_join_enabled;
            this.projectInvitationCode = res.data.invitation_code ?? null;
            // No role set means link joiners get viewer (the backend's fallback), so that is what shows.
            this.projectDefaultRole = res.data.default_role_name ?? 'viewer';
          }
        },
        error: () => {
          this.projectDetailLoading = false;
        },
      });
  }

  private loadProjectMembersDetail(projectId: string): void {
    this.projectMembersLoading = true;
    this.projectMembersError = false;
    this.projectApiService
      .getProjectMembersDetail(projectId)
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe({
        next: (res) => {
          this.projectMembersDetail = res.data ?? [];
          this.projectMembersLoading = false;
        },
        error: () => {
          this.projectMembersError = true;
          this.projectMembersLoading = false;
        },
      });
  }

  // ── Projects → General: editable member role dropdown ─────────
  get canEditMemberRoles(): boolean {
    return this.isProjectAdmin;
  }

  private loadProjectRoles(): void {
    if (this.projectRolesLoaded) return;
    this.projectRolesLoading = true;
    this.projectApiService.getProjectRoles().subscribe({
      next: (res) => {
        this.projectRoleOptions = (res.data ?? []).map((r) => ({
          id: r.id,
          label: this.roleLabels[r.name] ?? r.name,
        }));
        this.projectRolesLoaded = true;
        this.projectRolesLoading = false;
      },
      error: () => {
        this.projectRolesLoading = false;
      },
    });
  }

  /** Rows show as soon as both the members and the role options are in, so the selects never pop in afterwards. */
  get projectMembersTableLoading(): boolean {
    return this.projectMembersLoading || (this.canEditMemberRoles && this.projectRolesLoading);
  }

  /** The ProjectMemberRole id a role PATCH targets — looked up on first change and cached, as for org members. */
  private projectMemberRoleRowId$(memberId: string): Observable<string> {
    const cached = this.memberRoleRowIds.get(memberId);
    if (cached) return of(cached);
    return this.projectApiService.getProjectMemberRole(memberId).pipe(
      map((res) => {
        if (!res?.data) throw new Error('This member has no project role to change.');
        this.memberRoleRowIds.set(memberId, res.data.id);
        return res.data.id;
      }),
    );
  }

  isSavingMemberRole(memberId: string): boolean {
    return this.savingMemberRoleIds.has(memberId);
  }

  memberRoleOptionId(member: ProjectMemberDetailDto): string | null {
    return this.projectRoleOptions.find((o) => o.label.toLowerCase() === member.role)?.id ?? null;
  }

  onMemberRoleChange(member: ProjectMemberDetailDto, roleId: string | null): void {
    if (!roleId) return;

    const previousRole = member.role;
    const optimisticLabel = this.projectRoleOptions.find((o) => o.id === roleId)?.label.toLowerCase();
    member.role = (optimisticLabel ?? previousRole) as ProjectMemberDetailDto['role'];
    this.savingMemberRoleIds.add(member.id);

    this.projectMemberRoleRowId$(member.id)
      .pipe(switchMap((rowId) => this.projectApiService.updateProjectMemberRole(rowId, roleId)))
      .subscribe({
        next: (res) => {
          this.savingMemberRoleIds.delete(member.id);
          const confirmedLabel = this.projectRoleOptions
            .find((o) => o.id === res?.data?.project_role)
            ?.label.toLowerCase();
          if (confirmedLabel) member.role = confirmedLabel as ProjectMemberDetailDto['role'];
          this.toastService.success({ message: 'Member role updated.' });
        },
        error: (err) => {
          this.savingMemberRoleIds.delete(member.id);
          member.role = previousRole;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  saveProjectName(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    const value = this.projectName.trim();
    if (!value || value === this.lastSavedProjectName) {
      this.projectName = this.lastSavedProjectName;
      return;
    }

    this.projectApiService.patchProject(projectId, { name: value }).subscribe({
      next: () => {
        this.lastSavedProjectName = value;
        this.syncProjectListEntry(projectId, { name: value });
        this.toastService.success({ message: 'Project updated.' });
      },
      error: (err) => {
        this.projectName = this.lastSavedProjectName;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  onProjectIconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadProjectIcon(file);
    }
  }

  private uploadProjectIcon(file: File): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error({ message: 'Please choose an image file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => (this.projectIconPreview = e.target?.result as string);
    reader.readAsDataURL(file);

    this.isUploadingProjectIcon = true;
    this.requestService
      .getPresignedUploadUrl({
        filename: file.name,
        content_type: file.type,
        prefix: 'projects',
        project_id: projectId,
      })
      .subscribe({
        next: (resp) => {
          if (resp.status === 'success' && resp.data) {
            const key = resp.data.key;
            this.requestService.uploadFileToPresignedUrl(resp.data.upload_url, file).subscribe({
              next: () => {
                this.projectApiService.patchProject(projectId, { logo_key: key }).subscribe({
                  next: (updateResp) => {
                    const logo = updateResp.data?.logo ?? '';
                    this.syncProjectListEntry(projectId, { logo });
                    this.projectIconPreview = logo;
                    this.isUploadingProjectIcon = false;
                    this.toastService.success({ message: 'Project icon updated.' });
                  },
                  error: (err) => {
                    this.isUploadingProjectIcon = false;
                    this.projectIconPreview = null;
                    this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
                  },
                });
              },
              error: (err) => {
                this.isUploadingProjectIcon = false;
                this.projectIconPreview = null;
                this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
              },
            });
          } else {
            this.isUploadingProjectIcon = false;
            this.projectIconPreview = null;
            this.toastService.error({ message: 'Could not start the upload. Please try again.' });
          }
        },
        error: (err) => {
          this.isUploadingProjectIcon = false;
          this.projectIconPreview = null;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  /** `null` is Unassigned. */
  setDefaultAssignee(memberId: string | null): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    const previous = this.defaultAssigneeId;
    this.defaultAssigneeId = memberId;
    this.isSavingDefaultAssignee = true;

    this.projectApiService.patchProject(projectId, { default_assignee_id: memberId }).subscribe({
      next: () => {
        this.isSavingDefaultAssignee = false;
        this.toastService.success({ message: 'Default assignee updated.' });
      },
      error: (err) => {
        this.isSavingDefaultAssignee = false;
        this.defaultAssigneeId = previous;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  toggleMemberMenu(memberId: string): void {
    this.openMemberMenuId = this.openMemberMenuId === memberId ? null : memberId;
  }

  closeMemberMenu(): void {
    this.openMemberMenuId = null;
  }

  /** A click on the menu's own trigger is outside the overlay too; the trigger's toggle handles that one. */
  onMemberMenuOutsideClick(event: MouseEvent, trigger: HTMLElement): void {
    if (event.target instanceof Node && trigger.contains(event.target)) return;
    this.closeMemberMenu();
  }

  onMemberMenuKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    // Keep Escape from also reaching the settings modal, which would close it.
    event.stopPropagation();
    this.closeMemberMenu();
  }

  /** Admins can remove members/viewers, but not other admins. */
  canRemoveProjectMember(member: ProjectMemberDetailDto): boolean {
    return this.isProjectAdmin && member.role !== 'admin';
  }

  async requestRemoveMember(member: ProjectMemberDetailDto): Promise<void> {
    this.closeMemberMenu();
    const confirmed = await this.dialogs.confirm({
      title: `Remove ${member.name}?`,
      message: "They'll lose access to this project immediately. This can't be undone.",
      confirmLabel: 'Remove',
      tone: 'destructive',
    });
    if (confirmed) this.removeProjectMember(member);
  }

  private removeProjectMember(member: ProjectMemberDetailDto): void {
    this.openMemberMenuId = null;
    this.projectApiService.deleteProjectMember(member.id).subscribe({
      next: () => {
        this.projectMembersDetail = this.projectMembersDetail.filter((m) => m.id !== member.id);
        if (this.pagedProjectMembers.length === 0 && this.projectMemberCurrentPage > 1) {
          this.projectMemberCurrentPage--;
        }
        this.toastService.success({ message: 'Member removed.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  // ── Projects → Danger zone: delete project ─────────────────────
  get canDeleteProject(): boolean {
    return this.isProjectAdmin;
  }

  get canConfirmDeleteProject(): boolean {
    return this.deleteProjectConfirmText.trim().toUpperCase() === this.selectedProjectName.trim().toUpperCase();
  }

  openDeleteProjectConfirm(): void {
    this.deleteProjectConfirmOpen = true;
    this.deleteProjectConfirmText = '';
    this.deleteProjectError = null;
  }

  closeDeleteProjectConfirm(): void {
    if (this.isDeletingProject) return;
    this.deleteProjectConfirmOpen = false;
    this.deleteProjectConfirmText = '';
    this.deleteProjectError = null;
  }

  confirmDeleteProject(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || !this.canConfirmDeleteProject || this.isDeletingProject) return;

    this.isDeletingProject = true;
    this.deleteProjectError = null;
    this.projectApiService.deleteProject(projectId).subscribe({
      next: () => {
        this.isDeletingProject = false;
        this.deleteProjectConfirmOpen = false;
        this.toastService.success({ message: 'Project deleted.' });
        this.projectRemoved.emit(projectId);
        this.closeModal.emit();
      },
      error: (err) => {
        this.isDeletingProject = false;
        this.deleteProjectError = this.errorHandler.getUserFriendlyErrorMessage(err);
      },
    });
  }

  // ── Projects → Danger zone: leave project ───────────────────────
  async openLeaveProjectConfirm(): Promise<void> {
    if (this.isLeavingProject) return;
    const confirmed = await this.dialogs.confirm({
      title: `Leave "${this.selectedProjectName}"?`,
      message: "You'll lose access to all of its content. You can only rejoin if you're invited again.",
      confirmLabel: 'Leave project',
      tone: 'destructive',
    });
    if (confirmed) this.confirmLeaveProject();
  }

  confirmLeaveProject(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || this.isLeavingProject) return;

    this.isLeavingProject = true;
    this.projectApiService.leaveProject(projectId).subscribe({
      next: () => {
        this.isLeavingProject = false;
        this.toastService.success({ message: 'You left the project.' });
        this.projectRemoved.emit(projectId);
        this.closeModal.emit();
      },
      error: (err) => {
        this.isLeavingProject = false;
        // The sole-admin 403 message from the backend is already actionable — show it as-is.
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  // ── Projects → General: Invite link ───────────────────────────
  toggleProjectLinkJoin(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || this.isUpdatingProjectLinkJoin) return;

    const newValue = !this.projectLinkJoinEnabled;
    this.projectLinkJoinEnabled = newValue;
    this.isUpdatingProjectLinkJoin = true;

    this.projectApiService.updateProjectLinkSettings(projectId, { link_join_enabled: newValue }).subscribe({
      next: (res) => {
        this.isUpdatingProjectLinkJoin = false;
        if (res?.status === 'success' && res.data) {
          this.projectLinkJoinEnabled = res.data.link_join_enabled;
          this.projectInvitationCode = res.data.invitation_code;
        }
      },
      error: (err) => {
        this.isUpdatingProjectLinkJoin = false;
        this.projectLinkJoinEnabled = !newValue;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  copyProjectInviteLink(): void {
    if (!this.projectLinkJoinEnabled || !this.projectInvitationCode) return;
    const url = `${window.location.origin}/projects/join?code=${this.projectInvitationCode}`;
    navigator.clipboard.writeText(url).then(
      () => this.toastService.success({ message: 'Invite link copied.' }),
      () => this.toastService.error({ message: 'Could not copy the link.' }),
    );
  }

  regenerateProjectInviteLink(): void {
    const projectId = this.selectedProjectId;
    if (!projectId || this.isRegeneratingProjectLink) return;

    this.isRegeneratingProjectLink = true;
    this.projectApiService.regenerateProjectLink(projectId).subscribe({
      next: (res) => {
        this.isRegeneratingProjectLink = false;
        if (res?.status === 'success' && res.data) {
          this.projectInvitationCode = res.data.invitation_code;
          this.toastService.success({ message: 'Invite link regenerated.' });
        }
      },
      error: (err) => {
        this.isRegeneratingProjectLink = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  onProjectDefaultRoleChange(roleId: string | null): void {
    const projectId = this.selectedProjectId;
    if (!projectId || !roleId) return;

    const role = roleId as 'admin' | 'member' | 'viewer';
    const previous = this.projectDefaultRole;
    this.projectDefaultRole = role;
    this.isSavingProjectDefaultRole = true;

    this.projectApiService.updateProjectLinkSettings(projectId, { default_role: role }).subscribe({
      next: (res) => {
        this.isSavingProjectDefaultRole = false;
        if (res?.status === 'success' && res.data) {
          this.projectDefaultRole = res.data.default_role_name ?? role;
        }
        this.toastService.success({ message: 'Default role updated.' });
      },
      error: (err) => {
        this.isSavingProjectDefaultRole = false;
        this.projectDefaultRole = previous;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  // ── Projects → General: Integrations ──────────────────────────
  private loadIntegrationStatuses(projectId: string): void {
    this.loadSlackStatus(projectId);
    this.loadWebflowStatus(projectId);
    this.loadGchatStatus(projectId);
    // The HubSpot row is hidden in production, so there is nothing to load it for.
    if (!this.isProd) {
      this.loadHubspotStatus(projectId);
    }
  }

  private loadSlackStatus(projectId: string): void {
    this.slackLoading = true;
    this.slackService
      .getProjectStatus(projectId)
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe({
        next: (res) => {
          // The organization having a workspace says nothing about this project. What connects a
          // project is its own channel, and reading `org_connected` as connected offered people a
          // Disconnect button for something they had never connected.
          const channel = res.data?.channel ?? null;
          this.slackConnected = !!res.data?.project_installation;
          this.slackChannelName = channel?.channel_name ?? '';
          this.slackWorkspaceLinks = res.data?.workspace_links ?? [];
          this.slackSavedWorkspaceLinkId = this.hasSeveralSlackWorkspaceLinks
            ? (res.data?.project_installation?.id ?? '')
            : '';
          this.slackSavedChannelId = channel?.channel_id ?? '';
          this.slackSavedNotifyOnCreation = channel?.notify_on_creation ?? true;
          this.slackSavedNotifyOnCompletion = channel?.notify_on_completion ?? true;
          this.slackSelectedWorkspaceLinkId = this.slackSavedWorkspaceLinkId;
          this.slackLoading = false;

          if (this.pendingSlackSettings) {
            this.pendingSlackSettings = false;
            this.openSlackSettings(this.initialSlackWorkspaceLinkId);
            // Walking back from the install screen is the start of setting Slack up, never a
            // request to have no channel — whatever the project looks like mid-round-trip.
            this.slackNotificationsDisabled = false;
          }
        },
        error: () => {
          this.pendingSlackSettings = false;
          this.slackConnected = false;
          this.slackWorkspaceLinks = [];
          this.slackLoading = false;
        },
      });
  }

  /** Re-read which workspaces the organization has and what this project is connected to. */
  refreshSlackStatus(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;
    this.loadSlackStatus(projectId);
  }

  /**
   * Whether the organization is linked to more than one Slack workspace — the case a Client
   * organization is normally in, with the agency's workspace alongside its own.
   */
  get hasSeveralSlackWorkspaceLinks(): boolean {
    return this.slackWorkspaceLinks.length > 1;
  }

  /** Whether there is a workspace to pick a channel from at all, or the row has to install one. */
  get canPickSlackChannel(): boolean {
    return this.slackWorkspaceLinks.length > 0;
  }

  /**
   * Open the dialog on the saved setup, whether the project is connected already or not. A
   * workspace link named by an OAuth return wins over the saved one — it is the workspace the
   * user just went and installed, so it is the one they mean to point the project at.
   */
  openSlackSettings(installedWorkspaceLinkId?: string): void {
    const installed =
      installedWorkspaceLinkId && this.slackWorkspaceLinks.some((link) => link.id === installedWorkspaceLinkId)
        ? installedWorkspaceLinkId
        : '';

    this.slackSettingsOpen = true;
    // Never named when the organization has a single link: those requests stay what they were
    // before a project could choose, and there is nothing to choose between anyway.
    this.slackSelectedWorkspaceLinkId =
      this.hasSeveralSlackWorkspaceLinks && installed ? installed : this.slackSavedWorkspaceLinkId;
    // A channel saved against a different workspace does not exist in the one just installed.
    this.slackSelectedChannelId =
      this.slackSelectedWorkspaceLinkId === this.slackSavedWorkspaceLinkId ? this.slackSavedChannelId : '';
    this.slackNotifyOnCreation = this.slackSavedNotifyOnCreation;
    this.slackNotifyOnCompletion = this.slackSavedNotifyOnCompletion;
    // A project sitting on a workspace with no channel is one whose notifications were turned off.
    this.slackNotificationsDisabled = this.slackConnected && !this.slackChannelName;
    this.slackChannels = [];
    // With several workspaces and none chosen there is no channel list to show yet; otherwise the
    // channels of the one in hand are what the dialog needs.
    if (!this.hasSeveralSlackWorkspaceLinks || this.slackSelectedWorkspaceLinkId) {
      this.loadSlackChannels();
    }
  }

  closeSlackSettings(): void {
    this.slackSettingsOpen = false;
    this.slackChannels = [];
    this.slackSelectedChannelId = '';
    this.slackSelectedWorkspaceLinkId = this.slackSavedWorkspaceLinkId;
  }

  /** Whether the dialog holds something the backend can be told: a channel, or the decision to have none. */
  get canSaveSlackSettings(): boolean {
    return this.slackNotificationsDisabled || !!this.slackSelectedChannelId;
  }

  /** Point the picker at one of the organization's workspaces and list that one's channels. */
  selectSlackWorkspaceLink(workspaceLinkId: string): void {
    this.slackSelectedWorkspaceLinkId = workspaceLinkId;
    // The channel picked belonged to the previous workspace and does not exist in this one.
    this.slackSelectedChannelId = '';
    this.slackChannels = [];
    if (workspaceLinkId) {
      this.loadSlackChannels();
    }
  }

  private loadSlackChannels(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    this.slackChannelsLoading = true;
    this.slackService
      .getProjectChannels(projectId, this.slackSelectedWorkspaceLinkId || undefined)
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe({
        next: (res) => {
          this.slackChannels = res.data?.channels ?? [];
          this.slackChannelsLoading = false;
        },
        error: (err) => {
          this.slackChannelsLoading = false;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  saveSlackChannel(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    const channel = this.slackNotificationsDisabled
      ? undefined
      : this.slackChannels.find((c) => c.id === this.slackSelectedChannelId);
    // Turning notifications off is the same request as connecting with no channel in it: the
    // project keeps its workspace link and simply has nowhere left to post.
    const body: SlackProjectChannelRequestDto = {
      channel: channel
        ? {
            channel_id: channel.id,
            channel_name: channel.name,
            notify_on_creation: this.slackNotifyOnCreation,
            notify_on_completion: this.slackNotifyOnCompletion,
            // Absent unless the organization has several links, so a project in an organization with
            // one workspace sends the body it would have sent before this picker existed.
            ...(this.slackSelectedWorkspaceLinkId ? { workspace_link_id: this.slackSelectedWorkspaceLinkId } : {}),
          }
        : null,
    };

    const wasConnected = this.slackConnected;

    this.slackConnecting = true;
    this.slackService.setProjectChannel(projectId, body).subscribe({
      next: () => {
        this.slackConnecting = false;
        this.slackSettingsOpen = false;
        this.slackSavedWorkspaceLinkId = this.slackSelectedWorkspaceLinkId;
        this.slackSavedChannelId = body.channel?.channel_id ?? '';
        this.slackSavedNotifyOnCreation = this.slackNotifyOnCreation;
        this.slackSavedNotifyOnCompletion = this.slackNotifyOnCompletion;
        this.slackChannelName = body.channel?.channel_name ?? '';
        // Losing the channel does not unlink the workspace, so a connected project stays connected.
        this.slackConnected = true;
        this.toastService.success({
          message: wasConnected ? 'Project connection updated.' : 'Project connected to Slack.',
        });
      },
      error: (err) => {
        this.slackConnecting = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  /** Install another Slack workspace for the organization. The only path when it has none. */
  connectSlack(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId) return;

    this.slackLoading = true;
    this.slackService.getOAuthInstallUrl(orgId, projectId).subscribe({
      next: (res) => {
        // this.slackLoading = false;
        if (res.data?.url) {
          // Same tab, not a popup: Slack sends the user to /slack-oauth when it is done, and that
          // has to land in the tab they started in for them to be back where they left off.
          window.location.href = res.data.url;
        }
      },
      error: (err) => {
        this.slackLoading = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  /**
   * Unhook this project only. The organization keeps its workspace links, and other projects
   * using them are untouched.
   */
  disconnectSlack(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    this.slackService.disconnectProjectSlack(projectId).subscribe({
      next: () => {
        this.slackConnected = false;
        this.slackChannelName = '';
        this.slackSelectedChannelId = '';
        this.slackSelectedWorkspaceLinkId = '';
        this.slackSavedWorkspaceLinkId = '';
        this.slackSavedChannelId = '';
        this.slackSavedNotifyOnCreation = true;
        this.slackSavedNotifyOnCompletion = true;
        this.toastService.success({ message: 'This project is no longer connected to Slack.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  // ── HubSpot ───────────────────────────────────────────────────
  /**
   * The row needs two answers: whether the organization has a HubSpot account at all, and
   * whether this project is attached to a company in it.
   */
  private loadHubspotStatus(projectId: string): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.hubspotLoading = true;
    forkJoin({
      links: this.hubspotService.getPortalLinks(orgId).pipe(
        map((res) => res.data ?? []),
        catchError(() => of<HubspotPortalLinkDto[]>([])),
      ),
      connection: this.hubspotService.getProjectConnection(projectId).pipe(
        map((res) => res.data?.connection ?? null),
        catchError(() => of(null)),
      ),
    })
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe(({ links, connection }) => {
        // The answer for a project the reader has since moved away from is not this row's.
        if (projectId !== this.selectedProjectId) return;

        // A broken link no longer reaches HubSpot; Connect re-authorizes it rather than adding one.
        this.hubspotPortalLinks = links.filter((link) => !link.is_broken);
        this.hubspotConnection = connection;
        this.hubspotLoading = false;

        if (this.pendingHubspotSettings) {
          this.pendingHubspotSettings = false;
          // Just back from connecting the account: attaching the project is the next step, and only
          // a project admin may take it.
          if (!connection && this.hubspotPortalLinks.length && this.canManageHubspot) {
            this.openHubspotSettings();
          }
        }
      });
  }

  refreshHubspotStatus(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;
    this.loadHubspotStatus(projectId);
  }

  get hubspotState(): 'loading' | 'no-account' | 'not-attached' | 'attached' | 'broken' {
    if (this.hubspotLoading) return 'loading';
    if (this.hubspotConnection) return this.hubspotConnection.is_broken ? 'broken' : 'attached';
    return this.hubspotPortalLinks.length ? 'not-attached' : 'no-account';
  }

  /** Attaching, changing and removing the project's connection are a project admin's. */
  get canManageHubspot(): boolean {
    return this.isProjectAdmin;
  }

  /** Connecting the organization's account is an org owner's or admin's. */
  get canInstallHubspot(): boolean {
    return this.session.isOrganizationAdmin();
  }

  /** Why Connect is disabled for this reader, or `''` when it is not. */
  get hubspotConnectBlockedReason(): string {
    if (this.hubspotState === 'no-account' && !this.canInstallHubspot) {
      return 'An organization owner or admin connects HubSpot first.';
    }
    if (this.hubspotState === 'not-attached' && !this.canManageHubspot) {
      return 'A project admin connects this project to HubSpot.';
    }
    return '';
  }

  get hubspotCaption(): string {
    const connection = this.hubspotConnection;
    if (!connection || this.hubspotLoading) return 'Turn Client tickets from HubSpot into Requests.';
    // The person its Requests are filed under is never shown, so the caption does not name them.
    if (connection.is_broken) return 'Paused';
    // Most projects list no companies: their sites' domains are what route tickets to them.
    const names = connection.companies.map((company) => company.name);
    return names.length
      ? `Tickets from ${new Intl.ListFormat('en', { type: 'conjunction' }).format(names)}`
      : "Tickets from its sites' domains";
  }

  /** Install HubSpot for the organization. The only path when it has no account. */
  connectHubspot(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId || !this.canInstallHubspot) return;

    this.hubspotConnecting = true;
    this.hubspotService.getOAuthStartUrl(orgId).subscribe({
      next: (res) => {
        if (res.data?.url) {
          // HubSpot's return names no project, so the one to come back to is kept for the guard.
          this.storage.setString(STORAGE_KEYS.integrations.hubspotReturnProjectId, projectId);
          // Same tab, as Slack does: the return has to land where the user started.
          window.location.href = res.data.url;
        } else {
          this.hubspotConnecting = false;
        }
      },
      error: (err) => {
        this.hubspotConnecting = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  async openHubspotSettings(): Promise<void> {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId || !this.canManageHubspot) return;

    const ref = this.dialogs.open<HubspotSettingsResult, HubspotSettingsData>(HubspotSettingsDialogComponent, {
      data: {
        orgId,
        organizationName: this.session.organizationName(),
        projectId,
        projectName: this.selectedProjectName,
        portalLinks: this.hubspotPortalLinks,
        connection: this.hubspotConnection,
        canDisconnectAccount: this.canInstallHubspot,
      },
      dataToken: HUBSPOT_SETTINGS_DATA,
      ariaLabelledBy: HUBSPOT_SETTINGS_TITLE_ID,
    });

    if (await ref.closed) {
      this.refreshHubspotStatus();
    }
  }

  /** Unhook this project only, after asking: the organization keeps its HubSpot account. */
  async disconnectHubspot(): Promise<void> {
    const projectId = this.selectedProjectId;
    if (!projectId || !this.hubspotConnection) return;

    const confirmed = await this.dialogs.confirm({
      title: `Remove HubSpot from ${this.selectedProjectName}?`,
      message:
        "Requests already created from HubSpot stay in this project, but stop syncing. New HubSpot tickets won't come in.",
      confirmLabel: 'Remove',
      tone: 'destructive',
    });
    if (!confirmed) return;

    this.hubspotLoading = true;
    this.hubspotService.removeProjectConnection(projectId).subscribe({
      next: () => {
        this.toastService.success({ message: 'This project is no longer connected to HubSpot.' });
        this.refreshHubspotStatus();
      },
      error: (err) => {
        this.hubspotLoading = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  private loadWebflowStatus(projectId: string): void {
    const orgId = this.session.organizationId();
    if (!orgId) return;

    this.webflowLoading = true;
    this.webflowService
      .getProjectStatus(orgId, projectId)
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe({
        next: (res) => {
          this.webflowConnected = !!res.data?.connected;
          const createdAt = res.data?.installation?.created_at;
          this.webflowConnectedSince = createdAt
            ? new Date(createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
            : '';
          this.webflowLoading = false;
        },
        error: () => {
          this.webflowConnected = false;
          this.webflowLoading = false;
        },
      });
  }

  connectWebflow(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId) return;

    this.webflowLoading = true;
    this.webflowService.getOAuthStartUrl(orgId, projectId).subscribe({
      next: (res) => {
        this.webflowLoading = false;
        if (res.data?.url) {
          window.open(res.data.url, '_blank');
        }
      },
      error: (err) => {
        this.webflowLoading = false;
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  disconnectWebflow(): void {
    const projectId = this.selectedProjectId;
    const orgId = this.session.organizationId();
    if (!projectId || !orgId) return;

    this.webflowService.disconnectProject(orgId, projectId).subscribe({
      next: () => {
        this.webflowConnected = false;
        this.webflowConnectedSince = '';
        this.toastService.success({ message: 'Webflow disconnected.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  private loadGchatStatus(projectId: string): void {
    this.gchatLoading = true;
    this.googleChatService
      .getProjectStatus(projectId)
      .pipe(takeUntil(this.projectSwitch$))
      .subscribe({
        next: (res) => {
          // `configured` is the server having a service account, true for every project alike; what
          // connects this one is its own space.
          this.gchatEnabled = res.data?.enabled !== false;
          this.gchatConnected = !!res.data?.space;
          this.gchatLoading = false;
        },
        error: () => {
          this.gchatConnected = false;
          this.gchatLoading = false;
        },
      });
  }

  openGchatPicker(): void {
    this.gchatPickerOpen = true;
    if (this.gchatSpaces.length === 0) {
      this.gchatSpacesLoading = true;
      this.googleChatService.listSpaces().subscribe({
        next: (res) => {
          this.gchatSpaces = res.data?.spaces ?? [];
          this.gchatSpacesLoading = false;
        },
        error: () => {
          this.gchatSpacesLoading = false;
          this.toastService.error({ message: 'Google Chat is not set up for this organization yet.' });
        },
      });
    }
  }

  cancelGchatPicker(): void {
    this.gchatPickerOpen = false;
    this.gchatSelectedSpaceName = '';
  }

  connectGchat(): void {
    const projectId = this.selectedProjectId;
    const space = this.gchatSpaces.find((s) => s.name === this.gchatSelectedSpaceName);
    if (!projectId || !space) return;

    this.gchatConnecting = true;
    this.googleChatService
      .setProjectSpace(projectId, { space_name: space.name, space_display_name: space.displayName })
      .subscribe({
        next: () => {
          this.gchatConnecting = false;
          this.gchatConnected = true;
          this.gchatPickerOpen = false;
          this.toastService.success({ message: 'Google Chat connected.' });
        },
        error: (err) => {
          this.gchatConnecting = false;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  disconnectGchat(): void {
    const projectId = this.selectedProjectId;
    if (!projectId) return;

    this.googleChatService.removeProjectSpace(projectId).subscribe({
      next: () => {
        this.gchatConnected = false;
        this.toastService.success({ message: 'Google Chat disconnected.' });
      },
      error: (err) => {
        this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
      },
    });
  }

  get userInitials(): string {
    return this.session.userInitials();
  }

  get userAvatar(): string | null {
    return this.avatarPreview ?? this.session.userAvatar();
  }

  get userName(): string {
    return this.session.userName();
  }

  get userEmail(): string {
    return this.session.userEmail();
  }

  onClose(): void {
    this.passwordFormOpen = false;
    this.pendingSlackSettings = false;
    this.pendingHubspotSettings = false;
    this.closeModal.emit();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  saveName(field: 'first_name' | 'last_name'): void {
    const user = this.session.profile();
    if (!user) return;

    const value = field === 'first_name' ? this.firstName.trim() : this.lastName.trim();
    const previousValue = user[field];
    if (value === previousValue || !value) {
      this.firstName = user.first_name;
      this.lastName = user.last_name;
      return;
    }

    this.onboardingService.updateUserProfile(user.id, { [field]: value }).subscribe({
      next: () => {
        this.session.patchProfile({ [field]: value });
        this.toastService.success({ message: 'Profile updated.' });
      },
      error: () => {
        this.firstName = user.first_name;
        this.lastName = user.last_name;
        this.toastService.error({ message: 'Could not update your profile. Please try again.' });
      },
    });
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.uploadAvatar(file);
    }
  }

  private uploadAvatar(file: File): void {
    const user = this.session.profile();
    if (!user) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.error({ message: 'Please choose an image file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => (this.avatarPreview = e.target?.result as string);
    reader.readAsDataURL(file);

    this.isUploadingAvatar = true;
    this.onboardingService
      .getPresignedUploadUrl({ filename: file.name, content_type: file.type, prefix: 'avatars' })
      .subscribe({
        next: (resp) => {
          if (resp.status === 'success' && resp.data) {
            const key = resp.data.key;
            this.onboardingService.uploadFileToPresignedUrl(resp.data.upload_url, file).subscribe({
              next: () => {
                this.onboardingService.updateUserProfile(user.id, { avatar_key: key }).subscribe({
                  next: (updateResp) => {
                    const avatar = updateResp.data?.avatar ?? null;
                    this.session.patchProfile({ avatar });
                    this.avatarPreview = avatar;
                    this.isUploadingAvatar = false;
                    this.toastService.success({ message: 'Profile picture updated.' });
                  },
                  error: (err) => {
                    this.isUploadingAvatar = false;
                    this.avatarPreview = null;
                    this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
                  },
                });
              },
              error: (err) => {
                this.isUploadingAvatar = false;
                this.avatarPreview = null;
                this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
              },
            });
          } else {
            this.isUploadingAvatar = false;
            this.avatarPreview = null;
            this.toastService.error({ message: 'Could not start the upload. Please try again.' });
          }
        },
        error: (err) => {
          this.isUploadingAvatar = false;
          this.avatarPreview = null;
          this.toastService.error({ message: this.errorHandler.getUserFriendlyErrorMessage(err) });
        },
      });
  }

  togglePasswordForm(): void {
    this.passwordFormOpen = !this.passwordFormOpen;
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }

  submitPasswordChange(): void {
    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.toastService.error({ message: 'Please fill in all password fields.' });
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.toastService.error({ message: 'New password and confirmation do not match.' });
      return;
    }

    this.passwordSubmitting = true;
    this.dashboardService.updatePassword(this.currentPassword, this.newPassword).subscribe({
      next: () => {
        this.passwordSubmitting = false;
        this.toastService.success({ message: 'Password updated.' });
        this.togglePasswordForm();
      },
      error: () => {
        this.passwordSubmitting = false;
        this.toastService.error({ message: 'Could not update your password. Please try again.' });
      },
    });
  }
}
